import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Paper,
  Tabs,
  Tab,
  Stack,
  Divider,
  Grid,
  Avatar,
  IconButton,
  Button,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Person as PersonIcon,
  Category as CategoryIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  Label as LabelIcon,
} from '@mui/icons-material';
import { formatDate, formatDateTime } from '../../../utils/formatters.js';

const typeColors = {
  Call: 'primary',
  Meeting: 'secondary',
  Email: 'info',
  FollowUp: 'warning',
  Research: 'default',
  General: 'success',
  Other: 'default',
};

function NoteDetail({ note, onEdit, onDelete }) {
  const [tabValue, setTabValue] = React.useState(0);

  if (!note) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          Select a note to view details
        </Typography>
      </Box>
    );
  }

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              Note Details
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Chip
                label={note.type || 'General'}
                color={typeColors[note.type] || 'default'}
                size="small"
              />
              {note.contact && (
                <Chip
                  icon={<PersonIcon />}
                  label={note.contact}
                  variant="outlined"
                  size="small"
                />
              )}
            </Stack>
          </Box>
          <Stack direction="row" spacing={1}>
            <IconButton onClick={() => onEdit(note)} color="primary">
              <EditIcon />
            </IconButton>
            <IconButton onClick={() => onDelete(note)} color="error">
              <DeleteIcon />
            </IconButton>
          </Stack>
        </Stack>

        {note.note && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {note.note}
          </Typography>
        )}
      </Paper>

      {/* Tabs */}
      <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Overview" />
          <Tab label="Timeline" />
          <Tab label="Tags" />
        </Tabs>

        {/* Tab Content */}
        <Box sx={{ flex: 1, p: 3, overflow: 'auto' }}>
          {tabValue === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Contact
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <PersonIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {note.contact || 'Not specified'}
                      </Typography>
                    </Stack>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Type
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CategoryIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {note.type || 'General'}
                      </Typography>
                    </Stack>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Author
                    </Typography>
                    <Typography variant="body2">
                      {note.author || 'Unknown'}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Date
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <ScheduleIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {note.date ? formatDate(note.date) : 'Not set'}
                      </Typography>
                    </Stack>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Tags
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {(note.tags || []).length > 0 ? (
                        note.tags.map((tag, index) => (
                          <Chip key={index} label={tag} size="small" variant="outlined" />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No tags
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Stack>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Created
                  </Typography>
                  <Typography variant="body2">
                    {note.createdAt ? formatDateTime(note.createdAt) : 'Unknown'}
                  </Typography>
                </Box>
                {note.updatedAt && note.updatedAt !== note.createdAt && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Last Updated
                    </Typography>
                    <Typography variant="body2">
                      {formatDateTime(note.updatedAt)}
                    </Typography>
                  </Box>
                )}
              </Grid>
            </Grid>
          )}

          {tabValue === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Note Timeline
              </Typography>
              <Stack spacing={2}>
                <Paper sx={{ p: 2 }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                      <DescriptionIcon fontSize="small" />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight="medium">
                        Note Created
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {note.createdAt ? formatDateTime(note.createdAt) : 'Unknown'}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>

                {note.updatedAt && note.updatedAt !== note.createdAt && (
                  <Paper sx={{ p: 2 }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar sx={{ bgcolor: 'info.main', width: 32, height: 32 }}>
                        <EditIcon fontSize="small" />
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight="medium">
                          Note Updated
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDateTime(note.updatedAt)}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                )}

                {note.date && (
                  <Paper sx={{ p: 2 }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar sx={{ bgcolor: 'warning.main', width: 32, height: 32 }}>
                        <ScheduleIcon fontSize="small" />
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight="medium">
                          Note Date Set
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(note.date)}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                )}
              </Stack>
            </Box>
          )}

          {tabValue === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Tags & Categories
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Note Type
                  </Typography>
                  <Chip
                    label={note.type || 'General'}
                    color={typeColors[note.type] || 'default'}
                    icon={<CategoryIcon />}
                  />
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Tags
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {(note.tags || []).length > 0 ? (
                      note.tags.map((tag, index) => (
                        <Chip
                          key={index}
                          label={tag}
                          size="medium"
                          variant="outlined"
                          icon={<LabelIcon />}
                        />
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No tags assigned to this note.
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Related Contact
                  </Typography>
                  {note.contact ? (
                    <Chip
                      label={note.contact}
                      icon={<PersonIcon />}
                      variant="outlined"
                      size="medium"
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No contact associated with this note.
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
}

export default NoteDetail;
